import React, { useCallback, useMemo, useState } from 'react';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { clsx } from 'clsx';
import Input from 'src/components/Input/Input';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { stringScheme } from 'src/utils/validation-schemes';
import { PlateEditor } from 'src/components/PlateEditor/PlateEditor';

export const CreateFAQPopup = ({
  trigger,
  addKnowledgeBaseSource,
  closeSourceModal = () => {},
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const { handleSubmit, control, setError, clearErrors, reset } = useForm({
    defaultValues: {
      question: '',
      answer: '',
    },
    resolver: yupResolver(
      object({
        question: stringScheme({ required: true, minLength: 3, noslash: true }).label(
          'Topic/Question',
        ),
        answer: stringScheme({ required: true, minLength: 3 }).label('Content/Answer'),
      }),
    ),
  });

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const submit = useCallback(
    (data) => {
      if (data.answer.replace(/[\s\u200B-\u200D\uFEFF]/g, '').trim().length <= 1) {
        setError('answer', { message: 'Field Required' });
        return;
      }

      const sanitizedQuestion = data.question.replace(/^\/+|\/+$/g, '').trim();
      const questionWithExtension = `${sanitizedQuestion}.faq`;

      if (questionWithExtension.startsWith('.')) {
        setError('question', { message: 'Invalid Format' });
        return;
      }

      addKnowledgeBaseSource({
        ...data,
        question: questionWithExtension,
      });
      closeSourceModal();
      reset();
      // resetEditor();
      close();
    },
    [addKnowledgeBaseSource, close, closeSourceModal, reset, setError],
  );

  const handleCancel = useCallback(() => {
    reset();
    close();
  }, [close, reset]);

  const handleOpenChange = useCallback(
    (nextOpen) => {
      setOpen(nextOpen);

      if (!nextOpen) {
        reset();
      }
    },
    [reset],
  );

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogTrigger
          disabled={disabled}
          asChild
          className={clsx(disabled && 'pointer-events-none opacity-50', 'cursor-pointer')}
        >
          {trigger}
        </DialogTrigger>
        <DialogContent closeButtonHidden>
          <DialogHeader>
            <DialogTitle>Add FAQ</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="content">
              <form id="create-new-document" onSubmit={handleSubmit(submit)}>
                <div className={'input-container'}>
                  <Controller
                    control={control}
                    name="question"
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Input
                        type={'text'}
                        label={'Topic/Question'}
                        placeholder={'Enter a question or FAQ name here'}
                        invalidMessage={error?.message}
                        value={value}
                        isValid={!error?.message}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
              </form>
              <div className={'input-container'}>
                <Controller
                  control={control}
                  name="answer"
                  render={({ field: { onChange }, fieldState: { error } }) => (
                    <PlateEditor initialValue={''} onChange={onChange} error={error} />
                  )}
                />
              </div>
              <div className="flex justify-center mt-5">
                <div className="flex gap-2.5">
                  <Button
                    variant="outline"
                    type="button"
                    className="min-w-24"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                  <Button form={'create-new-document'} className="min-w-24">
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
